import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getInviteLink } from '../../Requests/User/UserRequests'
import { removeTeamMember, updateMemberStatus } from '../../Requests/TeamRequests'
import { getTeamUuid, getUserUuid } from '../../utils/cookies'
import MemberAvatar from './MemberAvatar'
import SuccessPopup from '../PopupNotifications/SuccessPopup'

export const MEMBER_STATUSES = [
    "registado",
    "investigador_principal",
    "investigador_secundario",
    "visualizador",
]

function TeamMemberTable({ teamMembers, isAdmin = false }) {

    const { t } = useTranslation()
    const [copyModal, setCopyModal] = useState(false)
    const [members, setMembers] = useState(teamMembers)
    // Removal is destructive, so it asks once on the row before it fires.
    const [confirmingRemoval, setConfirmingRemoval] = useState(null)
    const [removing, setRemoving] = useState(null)

    const handleRemove = (member) => {
        setRemoving(member.uuid)
        removeTeamMember(getTeamUuid(), member.uuid).then(() => {
            setMembers((current) => current.filter((m) => m.uuid !== member.uuid))
        }).catch(() => {
        }).finally(() => {
            setRemoving(null)
            setConfirmingRemoval(null)
        })
    }

    const handleAvatarUploaded = (uuid, avatar_url) => {
        setMembers((current) => current.map((member) => member.uuid === uuid ? { ...member, avatar_url } : member))
    }

    const handleStatusChange = (member, status) => {
        const previous = member.status
        setMembers((current) => current.map((m) => m.uuid === member.uuid ? { ...m, status } : m))
        updateMemberStatus(getTeamUuid(), member.uuid, status).catch(() => {
            setMembers((current) => current.map((m) => m.uuid === member.uuid ? { ...m, status: previous } : m))
        })
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
                        <table className="min-w-full">
                            <thead>
                                <tr>
                                    <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 ">
                                        {t('Team.TeamMemberTable.NameColumn')}
                                    </th>
                                    <th scope="col" className="hidden md:table-cell  px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        {t('Team.TeamMemberTable.RoleColumn')}
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                        {t('Team.TeamMemberTable.StatusColumn')}
                                    </th>
                                    <th scope="col" className="relative py-3.5 pl-3 text-right">
                                        {t('Team.TeamMemberTable.ActionColumn')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {members.map((teamMember) => (
                                    <tr key={teamMember.email}>
                                        <td className="whitespace-nowrap py-5 pl-0 pr-3 text-sm sm:pl-0">
                                            <div className="flex items-center">
                                                <MemberAvatar member={teamMember} onUploaded={handleAvatarUploaded} />
                                                <div className="ml-4">
                                                    <div className="font-medium text-gray-900">{teamMember.name || t('Team.TeamMemberTable.MemberNotRegistered')}</div>
                                                    <div className="mt-1 text-gray-500">{teamMember.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="hidden md:table-cell whitespace-nowrap px-3 py-5 text-sm text-gray-500">{teamMember.role}</td>
                                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                                            <select
                                                value={teamMember.status || "registado"}
                                                onChange={(e) => handleStatusChange(teamMember, e.target.value)}
                                                className="rounded-lg border-gray-300 py-1.5 text-sm focus:border-black focus:ring-black"
                                            >
                                                {MEMBER_STATUSES.map((status) => (
                                                    <option key={status} value={status}>
                                                        {t(`Team.MemberStatus.${status}`)}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className=" relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                                            <div className="flex items-center justify-end gap-3">
                                                {!teamMember.name &&
                                                    <button className="text-black hover:text-black" onClick={() => {
                                                        getInviteLink(teamMember.uuid).then((res) => {
                                                            navigator.clipboard?.writeText(res.data)
                                                            setCopyModal(true)
                                                        }).catch((e) => {

                                                        })
                                                    }}>
                                                        {t('Team.TeamMemberTable.ActionButtonBeforeActive')}
                                                    </button>
                                                }
                                                {isAdmin && teamMember.uuid !== getUserUuid() && (
                                                    confirmingRemoval === teamMember.uuid
                                                        ? <span className="flex items-center gap-2">
                                                            <button
                                                                className="text-red-600 hover:text-red-700 disabled:opacity-50"
                                                                disabled={removing === teamMember.uuid}
                                                                onClick={() => handleRemove(teamMember)}
                                                            >
                                                                {t('Team.TeamMemberTable.ConfirmRemove')}
                                                            </button>
                                                            <button
                                                                className="text-gray-500 hover:text-gray-700"
                                                                onClick={() => setConfirmingRemoval(null)}
                                                            >
                                                                {t('Team.TeamMemberTable.CancelRemove')}
                                                            </button>
                                                        </span>
                                                        : <button
                                                            className="text-gray-600 hover:text-red-600"
                                                            onClick={() => setConfirmingRemoval(teamMember.uuid)}
                                                        >
                                                            {t('Team.TeamMemberTable.RemoveMember')}
                                                        </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {copyModal && <SuccessPopup text={t('Team.TeamMemberTable.CopyMoodalLabel')} handleClose={() => { setCopyModal(false) }} />}
        </div>
    )
}

export default TeamMemberTable
